import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { ListLoading } from '../../library/ListLoading'
import { Sentinel } from '../../library/Sentinel'
import { useMediaSrc } from '../../library/useMediaSrc'
import { ItemMenu, QueueCard, type QueueActions } from './QueueItem'
import { addDays, monthGrid, SCHEDULE_TYPES, type Month, type Ymd } from './queueDates'
import { groupByDay, type DayCount, type ScheduleItem } from './queueData'
import { dayLabel, formatTime, LONG_DAY as LONG, TYPE_ICON } from './queueFormat'

/** The month grid (Sunday first), a bar per item type with that day's count. */
export function Calendar({
  month,
  today,
  selected,
  counts,
  onPick,
}: {
  month: Month
  today: Ymd
  selected: Ymd | null
  counts: Record<Ymd, DayCount>
  onPick: (day: Ymd) => void
}) {
  const { t, i18n } = useTranslation('ofQueue')
  const weeks = monthGrid(month)
  const lang = i18n.language
  // 2026-10-04 was a Sunday.
  const weekdays = Array.from({ length: 7 }, (_, i) => dayLabel(addDays('2026-10-04', i), lang, { weekday: 'narrow' }))
  return (
    <div className="ofq-cal">
      <div className="ofq-cal-head" aria-hidden="true">
        {weekdays.map((w, i) => (
          <span key={i}>{w}</span>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="ofq-cal-week">
          {week.map((day) => {
            const inMonth = Number(day.slice(5, 7)) === month.month
            const count = counts[day]
            const past = day < today
            const n = Number(day.slice(8))
            return (
              <button
                key={day}
                type="button"
                className={`ofq-cell ${inMonth ? '' : 'is-out'} ${day === today ? 'is-today' : ''} ${day === selected ? 'is-on' : ''}`}
                disabled={past}
                aria-pressed={day === selected}
                aria-label={count ? t('dayCount', { date: dayLabel(day, lang, LONG), count: count.total }) : dayLabel(day, lang, LONG)}
                onClick={() => onPick(day)}
              >
                <span className="ofq-cell-n">{n === 1 ? dayLabel(day, lang, { month: 'short', day: 'numeric' }) : n}</span>
                {count &&
                  SCHEDULE_TYPES.filter((ty) => count[ty] > 0).map((ty) => (
                    <span key={ty} className={`ofq-bar ofq-bar--${ty}`} title={t(`type.${ty}`)}>
                      {count[ty]}
                    </span>
                  ))}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/** The right pane: "‹ October 9, 2026 ›" and that day's items. */
export function DayPane({
  day,
  today,
  items,
  loading,
  failed,
  onRetry,
  onStep,
  onClose,
  actions,
  mobile,
}: {
  day: Ymd
  today: Ymd
  items: ScheduleItem[]
  loading: boolean
  failed: boolean
  onRetry: () => void
  onStep: (d: number) => void
  onClose?: () => void
  actions: QueueActions
  mobile: boolean
}) {
  const { t, i18n } = useTranslation('ofQueue')
  return (
    <section className="ofq-pane" aria-label={t('day')}>
      <header className="ofq-pane-head">
        <button type="button" className="lib-icon-btn" aria-label={t('prevDay')} disabled={day <= today} onClick={() => onStep(-1)}>
          <FeatherIcon name="chevron-left" size={20} />
        </button>
        <h2 className="ofq-pane-title">{dayLabel(day, i18n.language, LONG)}</h2>
        <button type="button" className="lib-icon-btn" aria-label={t('nextDay')} onClick={() => onStep(1)}>
          <FeatherIcon name="chevron-right" size={20} />
        </button>
        {onClose && (
          <button type="button" className="lib-icon-btn" aria-label={t('close')} onClick={onClose}>
            <FeatherIcon name="x" size={20} />
          </button>
        )}
      </header>
      <div className="ofq-pane-body">
        {items.map((item) => (
          <QueueCard key={item.id} item={item} actions={actions} mobile={mobile} />
        ))}
        <ListLoading loading={loading} empty={!items.length} />
        {failed && <LoadFailed onRetry={onRetry} />}
        {!loading && !failed && !items.length && <p className="lib-empty">{t('emptyDay')}</p>}
      </div>
    </section>
  )
}

export function LoadFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('ofQueue')
  return (
    <div className="ofq-failed">
      <span>{t('loadFailed')}</span>
      <button type="button" className="lib-btn" onClick={onRetry}>
        {t('retry')}
      </button>
    </div>
  )
}

/** The list layout: rows grouped by day — time, type, media count, thumb, ···. */
export function QueueList({
  items,
  loading,
  failed,
  hasMore,
  onMore,
  selected,
  onPick,
  actions,
  mobile,
}: {
  items: ScheduleItem[]
  loading: boolean
  failed: boolean
  hasMore: boolean
  onMore: () => void
  selected: Ymd | null
  onPick: (day: Ymd, item: ScheduleItem) => void
  actions: QueueActions
  mobile: boolean
}) {
  const { t, i18n } = useTranslation('ofQueue')
  const groups = groupByDay(items, actions.timeZone)
  return (
    <div className="ofq-list" role="list" aria-label={t('list')}>
      {groups.map((g) => (
        <div key={g.day} className="ofq-lgroup" role="presentation">
          <div className="ofq-lday" aria-hidden="true">
            <b>{dayLabel(g.day, i18n.language, { month: 'short', day: 'numeric' })}</b>
            <span>{dayLabel(g.day, i18n.language, { weekday: 'short' })}</span>
          </div>
          <div className="ofq-lrows" role="presentation">
            {g.items.map((item) => (
              <div
                key={item.id}
                role="listitem"
                className={`ofq-lrow ${selected === g.day ? 'is-day' : ''}`}
                onClick={() => onPick(g.day, item)}
              >
                <div className="ofq-lrow-main">
                  <span className="ofq-lrow-time">
                    {formatTime(item.publishAt!, actions.timeZone, i18n.language)}
                    <FeatherIcon name={TYPE_ICON[item.type]} size={14} className="ofq-lrow-type" aria-label={t(`typeOne.${item.type}`)} />
                  </span>
                  <span className="ofq-lrow-meta" title={t('media', { count: item.mediaCount })}>
                    <FeatherIcon name="image" size={12} /> {item.mediaCount}
                  </span>
                </div>
                {item.media[0] && <RowThumb url={item.media[0].thumb || item.media[0].full} />}
                <span onClick={(e) => e.stopPropagation()}>
                  <ItemMenu item={item} actions={actions} mobile={mobile} />
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <ListLoading loading={loading} empty={!items.length} />
      {failed && <LoadFailed onRetry={onMore} />}
      {!loading && !failed && !hasMore && !items.length && <p className="lib-empty">{t('emptyMonth')}</p>}
      {hasMore && !failed && <Sentinel onVisible={onMore} token={items.length} />}
    </div>
  )
}

function RowThumb({ url }: { url: string }) {
  const { ref, src } = useMediaSrc(url)
  return (
    <span ref={ref as React.RefObject<HTMLSpanElement>} className="ofq-lrow-thumb" aria-hidden="true">
      {src && <img src={src} alt="" />}
    </span>
  )
}
